import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import StockMonitor from './StockMonitor';
import PurchaseOrders from './PurchaseOrders';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const RED = '#b32d2d';
const GREEN = '#1a7a4c';
const AMBER = '#b8860b';

const CARD = { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 20, marginBottom: 20 };
const LABEL = { display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 };
const INPUT = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL, boxSizing: 'border-box' };
const BTN = { padding: '9px 20px', background: GARNET, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const BTN_GHOST = { padding: '7px 14px', background: 'none', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: COAL };
const TH = { textAlign: 'left', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 10px', borderBottom: '1px solid #eee' };
const TD = { fontSize: 14, color: COAL, padding: '8px 10px', borderBottom: '1px solid #f5f5f5' };

const money = (v) => `HK$${Number(v || 0).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString('en-HK', { maximumFractionDigits: 3 });

const TABS = ['Warehouse', 'Receive', 'Transfers', 'Direct', 'Invoices', 'Supplies', 'Stock Monitor', 'Factory POs'];

export default function Operations() {
  const [tab, setTab] = useState('Warehouse');
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [toast, setToast] = useState(null);

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const loadItems = useCallback(async () => {
    try {
      const res = await api.get('/ops/items');
      setItems(res.data.items || []);
    } catch (e) { console.error(e); }
  }, []);

  // Studios stock can be shipped to. Data-driven, so a new studio (K11) shows up
  // here the moment it exists — nothing to edit in this file.
  const loadLocations = useCallback(async () => {
    try {
      const res = await api.get('/ops/stock-locations');
      setLocations(res.data.locations || []);
    } catch (e) { console.error(e); }
  }, []);

  // Create an item inline (used by line editors) and refresh the list.
  const createItem = useCallback(async (payload) => {
    const res = await api.post('/ops/items', payload);
    await loadItems();
    return res.data.item;
  }, [loadItems]);

  useEffect(() => { loadItems(); loadLocations(); }, [loadItems, loadLocations]);

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: toast.isError ? RED : GREEN, color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, zIndex: 2000, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          {toast.msg}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: COAL, fontSize: 22 }}>Operations — Inventory & Chargeback</h2>
        <div style={{ fontSize: 13, color: ASHEN, marginTop: 4 }}>
          Labret Group warehouse stock, transfers to each studio, and month-end intercompany invoices
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (tab === t ? GARNET : '#ddd'), background: tab === t ? GARNET : '#fff', color: tab === t ? '#fff' : COAL }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Warehouse' && <Warehouse items={items} reload={loadItems} showToast={showToast} />}
      {tab === 'Receive' && <Receive items={items} reload={loadItems} createItem={createItem} showToast={showToast} />}
      {tab === 'Transfers' && <Movement kind="transfer" items={items} locations={locations} reload={loadItems} createItem={createItem} showToast={showToast} />}
      {tab === 'Direct' && <Movement kind="direct" items={items} locations={locations} reload={loadItems} createItem={createItem} showToast={showToast} />}
      {tab === 'Invoices' && <Invoices showToast={showToast} />}
      {tab === 'Supplies' && <Supplies items={items} locations={locations} showToast={showToast} />}
      {tab === 'Stock Monitor' && <StockMonitor />}
      {tab === 'Factory POs' && <PurchaseOrders />}
    </div>
  );
}

// ── Warehouse: item master + stock + add/edit item ─────────────────────────────
const BLANK_ITEM = { name: '', category: 'jewelry', unit_of_measure: 'unit', purchase_currency: 'HKD', units_per_box: 1, sku: '', opening_stock: '', opening_unit_cost_native: '' };

function Warehouse({ items, reload, showToast }) {
  const [form, setForm] = useState(BLANK_ITEM);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post('/ops/items', form);
      showToast('Item added');
      setForm({ ...BLANK_ITEM, category: form.category, unit_of_measure: form.unit_of_measure, purchase_currency: form.purchase_currency });
      reload();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to add item', true);
    } finally { setSaving(false); }
  };

  return (
    <>
      <div style={CARD}>
        <div style={{ ...LABEL, marginBottom: 16 }}>Add item</div>
        <form onSubmit={add}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 14 }}>
            <div><label style={LABEL}>Name</label><input style={INPUT} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
            <div><label style={LABEL}>Category</label>
              <select style={INPUT} value={form.category} onChange={e => setForm({ ...form, category: e.target.value, unit_of_measure: e.target.value === 'supplies' ? 'box' : 'unit' })}>
                <option value="jewelry">Jewelry</option><option value="supplies">Supplies</option>
              </select>
            </div>
            <div><label style={LABEL}>Unit</label>
              <select style={INPUT} value={form.unit_of_measure} onChange={e => setForm({ ...form, unit_of_measure: e.target.value })}>
                <option value="unit">Unit</option><option value="box">Box</option>
              </select>
            </div>
            {form.category === 'supplies' && (
              <div><label style={LABEL}>Pieces / box</label><input type="number" min="1" style={INPUT} value={form.units_per_box} onChange={e => setForm({ ...form, units_per_box: e.target.value })} /></div>
            )}
            <div><label style={LABEL}>Buy currency</label>
              <select style={INPUT} value={form.purchase_currency} onChange={e => setForm({ ...form, purchase_currency: e.target.value })}>
                <option>HKD</option><option>CNY</option><option>USD</option><option>SGD</option>
              </select>
            </div>
            <div><label style={LABEL}>SKU (optional)</label><input style={INPUT} value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} /></div>
            <div><label style={LABEL}>Opening stock (optional)</label><input type="number" step="any" style={INPUT} value={form.opening_stock} onChange={e => setForm({ ...form, opening_stock: e.target.value })} /></div>
            <div><label style={LABEL}>Opening unit cost ({form.purchase_currency})</label><input type="number" step="any" style={INPUT} value={form.opening_unit_cost_native} onChange={e => setForm({ ...form, opening_unit_cost_native: e.target.value })} /></div>
          </div>
          <div style={{ fontSize: 12, color: ASHEN, marginBottom: 12 }}>Opening stock + cost are optional — they seed the warehouse quantity and average cost. Cost is converted to HKD at today's rate. You can also leave them blank and add stock via a Purchase Order.</div>
          <button style={BTN} disabled={saving}>{saving ? 'Saving…' : 'Add item'}</button>
        </form>
      </div>

      <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', ...LABEL, marginBottom: 0 }}>Warehouse stock ({items.length} items)</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead><tr><th style={TH}>Item</th><th style={TH}>Category</th><th style={TH}>Warehouse qty</th><th style={TH}>Avg cost (HKD)</th><th style={TH}>Currency</th><th style={TH}></th></tr></thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td style={{ ...TD, color: ASHEN }} colSpan={6}>No items yet.</td></tr>
              ) : items.map(it => (
                <tr key={it.id}>
                  <td style={TD}>{it.name}{it.sku ? <span style={{ color: ASHEN, fontSize: 12 }}> · {it.sku}</span> : null}</td>
                  <td style={TD}>{it.category}</td>
                  <td style={TD}>{num(it.warehouse_stock)} {it.unit_of_measure}</td>
                  <td style={TD}>{money(it.avg_cost_hkd)}</td>
                  <td style={TD}>{it.purchase_currency}</td>
                  <td style={TD}><button onClick={() => setEditItem(it)} style={BTN_GHOST}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editItem && <EditItemModal item={editItem} onClose={() => setEditItem(null)} reload={reload} showToast={showToast} />}
    </>
  );
}

function EditItemModal({ item, onClose, reload, showToast }) {
  const [f, setF] = useState({
    name: item.name, sku: item.sku || '', category: item.category,
    unit_of_measure: item.unit_of_measure, purchase_currency: item.purchase_currency,
    units_per_box: item.units_per_box,
    warehouse_stock: item.warehouse_stock, avg_cost_hkd: item.avg_cost_hkd,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/ops/items/${item.id}`, f);
      showToast('Item updated');
      onClose(); reload();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', true);
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 560, width: '100%', marginTop: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: GARNET }}>Edit item</h3>
          <button onClick={onClose} style={BTN_GHOST}>Close</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 14 }}>
          <div><label style={LABEL}>Name</label><input style={INPUT} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
          <div><label style={LABEL}>Category</label>
            <select style={INPUT} value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
              <option value="jewelry">Jewelry</option><option value="supplies">Supplies</option>
            </select>
          </div>
          <div><label style={LABEL}>Unit</label>
            <select style={INPUT} value={f.unit_of_measure} onChange={e => setF({ ...f, unit_of_measure: e.target.value })}>
              <option value="unit">Unit</option><option value="box">Box</option>
            </select>
          </div>
          <div><label style={LABEL}>Buy currency</label>
            <select style={INPUT} value={f.purchase_currency} onChange={e => setF({ ...f, purchase_currency: e.target.value })}>
              <option>HKD</option><option>CNY</option><option>USD</option><option>SGD</option>
            </select>
          </div>
          <div><label style={LABEL}>SKU</label><input style={INPUT} value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} /></div>
          {f.category === 'supplies' && <div><label style={LABEL}>Pieces / box</label><input type="number" min="1" style={INPUT} value={f.units_per_box} onChange={e => setF({ ...f, units_per_box: e.target.value })} /></div>}
          <div><label style={LABEL}>Warehouse stock</label><input type="number" step="any" style={INPUT} value={f.warehouse_stock} onChange={e => setF({ ...f, warehouse_stock: e.target.value })} /></div>
          <div><label style={LABEL}>Avg cost (HKD)</label><input type="number" step="any" style={INPUT} value={f.avg_cost_hkd} onChange={e => setF({ ...f, avg_cost_hkd: e.target.value })} /></div>
        </div>
        <div style={{ fontSize: 12, color: ASHEN, marginBottom: 14 }}>Editing stock/cost is a manual correction — it resets this item's average-cost basis to these figures.</div>
        <button style={BTN} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
      </div>
    </div>
  );
}

// ── Line editor (shared) ───────────────────────────────────────────────────────
function LineEditor({ items, lines, setLines, withCost, createItem, showToast }) {
  const [creating, setCreating] = useState(null); // line index awaiting a new item
  const [ni, setNi] = useState({ name: '', category: 'jewelry', purchase_currency: 'CNY' });
  const [busy, setBusy] = useState(false);
  const add = () => setLines([...lines, { item_id: '', quantity: '', unit_cost_native: '' }]);
  const upd = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const del = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const onSelect = (i, v) => {
    if (v === '__new__') { setCreating(i); setNi({ name: '', category: 'jewelry', purchase_currency: 'CNY' }); }
    else upd(i, 'item_id', v);
  };
  const saveNew = async () => {
    if (!ni.name.trim()) { showToast && showToast('Enter a name', true); return; }
    setBusy(true);
    try {
      const item = await createItem({ name: ni.name, category: ni.category, purchase_currency: ni.purchase_currency });
      upd(creating, 'item_id', item.id);
      setCreating(null);
    } catch (err) {
      showToast && showToast(err.response?.data?.error || 'Could not create item', true);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LABEL}>Items</label>
      {lines.map((l, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select style={{ ...INPUT, flex: 2, minWidth: 160 }} value={l.item_id} onChange={e => onSelect(i, e.target.value)}>
              <option value="">Select item…</option>
              {items.map(it => <option key={it.id} value={it.id}>{it.name} ({it.category})</option>)}
              {createItem && <option value="__new__">＋ New item…</option>}
            </select>
            <input style={{ ...INPUT, flex: 1, minWidth: 90 }} type="number" step="any" placeholder="Qty" value={l.quantity} onChange={e => upd(i, 'quantity', e.target.value)} />
            {withCost && <input style={{ ...INPUT, flex: 1, minWidth: 110 }} type="number" step="any" placeholder="Unit cost" value={l.unit_cost_native} onChange={e => upd(i, 'unit_cost_native', e.target.value)} />}
            <button type="button" onClick={() => del(i)} style={{ ...BTN_GHOST, padding: '7px 10px' }}>×</button>
          </div>
          {creating === i && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, padding: 10, background: '#faf7f7', borderRadius: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input style={{ ...INPUT, flex: 2, minWidth: 140 }} placeholder="New item name" value={ni.name} onChange={e => setNi({ ...ni, name: e.target.value })} />
              <select style={{ ...INPUT, flex: 1, minWidth: 110 }} value={ni.category} onChange={e => setNi({ ...ni, category: e.target.value })}>
                <option value="jewelry">Jewelry</option><option value="supplies">Supplies</option>
              </select>
              <select style={{ ...INPUT, flex: 1, minWidth: 90 }} value={ni.purchase_currency} onChange={e => setNi({ ...ni, purchase_currency: e.target.value })}>
                <option>HKD</option><option>CNY</option><option>USD</option><option>SGD</option>
              </select>
              <button type="button" style={BTN} onClick={saveNew} disabled={busy}>{busy ? 'Adding…' : 'Create'}</button>
              <button type="button" style={BTN_GHOST} onClick={() => setCreating(null)}>Cancel</button>
            </div>
          )}
        </div>
      ))}
      <button type="button" onClick={add} style={BTN_GHOST}>+ Add line</button>
    </div>
  );
}

// ── Receive (PO) ───────────────────────────────────────────────────────────────
function Receive({ items, reload, createItem, showToast }) {
  const [supplier, setSupplier] = useState('');
  const [reference, setReference] = useState('');
  const [currency, setCurrency] = useState('CNY');
  const [fx, setFx] = useState('');
  const [fxAuto, setFxAuto] = useState(null);
  const [lines, setLines] = useState([{ item_id: '', quantity: '', unit_cost_native: '' }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ok = true;
    api.get(`/ops/fx-rate?from=${currency}`).then(r => { if (ok) setFxAuto(r.data.rate); }).catch(() => setFxAuto(null));
    return () => { ok = false; };
  }, [currency]);

  const submit = async (e) => {
    e.preventDefault();
    const valid = lines.filter(l => l.item_id && l.quantity);
    if (!valid.length) { showToast('Add at least one item line', true); return; }
    setSaving(true);
    try {
      const res = await api.post('/ops/purchase-orders', {
        supplier, reference, currency,
        fx_rate_to_hkd: fx || undefined,
        lines: valid,
      });
      showToast(`PO logged — ${money(res.data.total_cost_hkd)} @ FX ${res.data.fx_rate_to_hkd}`);
      setSupplier(''); setReference(''); setFx(''); setLines([{ item_id: '', quantity: '', unit_cost_native: '' }]);
      reload();
    } catch (err) {
      showToast(err.response?.data?.error || 'PO failed', true);
    } finally { setSaving(false); }
  };

  return (
    <div style={CARD}>
      <div style={{ ...LABEL, marginBottom: 16 }}>Receive stock (Purchase Order → warehouse)</div>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 14 }}>
          <div><label style={LABEL}>Supplier</label><input style={INPUT} value={supplier} onChange={e => setSupplier(e.target.value)} /></div>
          <div><label style={LABEL}>PO reference</label><input style={INPUT} value={reference} onChange={e => setReference(e.target.value)} /></div>
          <div><label style={LABEL}>Currency</label>
            <select style={INPUT} value={currency} onChange={e => setCurrency(e.target.value)}>
              <option>HKD</option><option>CNY</option><option>USD</option><option>SGD</option>
            </select>
          </div>
          <div>
            <label style={LABEL}>FX → HKD {fxAuto != null && <span style={{ color: ASHEN, textTransform: 'none' }}>(auto {fxAuto})</span>}</label>
            <input style={INPUT} type="number" step="any" placeholder={fxAuto != null ? String(fxAuto) : 'rate'} value={fx} onChange={e => setFx(e.target.value)} />
          </div>
        </div>
        <LineEditor items={items} lines={lines} setLines={setLines} withCost createItem={createItem} showToast={showToast} />
        <button style={BTN} disabled={saving}>{saving ? 'Saving…' : 'Log purchase order'}</button>
      </form>
    </div>
  );
}

// ── Transfers / Direct shipments ───────────────────────────────────────────────
function Movement({ kind, items, locations, reload, createItem, showToast }) {
  const isDirect = kind === 'direct';
  const [dest, setDest] = useState('');
  const [supplier, setSupplier] = useState('');
  const [currency, setCurrency] = useState('CNY');
  const [fx, setFx] = useState('');
  const [shipMethod, setShipMethod] = useState('');
  const [shipCost, setShipCost] = useState('');
  const [payer, setPayer] = useState('GROUP');
  const [lines, setLines] = useState([{ item_id: '', quantity: '', unit_cost_native: '' }]);
  const [saving, setSaving] = useState(false);

  // Default to the first studio once the list arrives (no hardcoded location).
  useEffect(() => { if (!dest && locations.length) setDest(locations[0].id); }, [dest, locations]);

  const submit = async (e) => {
    e.preventDefault();
    if (!dest) { showToast('Choose a destination studio', true); return; }
    const valid = lines.filter(l => l.item_id && l.quantity);
    if (!valid.length) { showToast('Add at least one item line', true); return; }
    setSaving(true);
    try {
      const body = {
        destination_studio_id: dest,
        shipping_method: shipMethod, shipping_cost: shipCost || 0, shipping_payer: payer,
        lines: valid,
      };
      if (isDirect) { body.supplier = supplier; body.currency = currency; body.fx_rate_to_hkd = fx || undefined; }
      const url = isDirect ? '/ops/direct-shipments' : '/ops/transfers';
      await api.post(url, body);
      const to = locations.find(l => l.id === dest);
      showToast(`${isDirect ? 'Direct shipment' : 'Transfer'} recorded${to ? ` → ${to.name}` : ''}`);
      setLines([{ item_id: '', quantity: '', unit_cost_native: '' }]); setShipCost(''); setShipMethod('');
      reload();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed', true);
    } finally { setSaving(false); }
  };

  return (
    <div style={CARD}>
      <div style={{ ...LABEL, marginBottom: 16 }}>
        {isDirect ? 'Direct supplier shipment (Taobao/supplier → studio, bypasses warehouse)' : 'Transfer (warehouse → studio)'}
      </div>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 14 }}>
          <div><label style={LABEL}>Destination studio</label>
            <select style={INPUT} value={dest} onChange={e => setDest(e.target.value)}>
              {locations.length === 0 && <option value="">No studios available</option>}
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name} ({l.country})</option>
              ))}
            </select>
          </div>
          {isDirect && <>
            <div><label style={LABEL}>Supplier</label><input style={INPUT} value={supplier} onChange={e => setSupplier(e.target.value)} /></div>
            <div><label style={LABEL}>Currency</label>
              <select style={INPUT} value={currency} onChange={e => setCurrency(e.target.value)}>
                <option>HKD</option><option>CNY</option><option>USD</option><option>SGD</option>
              </select>
            </div>
            <div><label style={LABEL}>FX → HKD</label><input style={INPUT} type="number" step="any" placeholder="auto if blank" value={fx} onChange={e => setFx(e.target.value)} /></div>
          </>}
          <div><label style={LABEL}>Shipping method</label><input style={INPUT} value={shipMethod} onChange={e => setShipMethod(e.target.value)} /></div>
          <div><label style={LABEL}>Shipping cost (HKD)</label><input style={INPUT} type="number" step="any" value={shipCost} onChange={e => setShipCost(e.target.value)} /></div>
          <div><label style={LABEL}>Shipping paid by</label>
            <select style={INPUT} value={payer} onChange={e => setPayer(e.target.value)}>
              <option value="GROUP">Labret Group</option><option value="HK">Labret HK</option><option value="SG">Labret SG</option>
            </select>
          </div>
        </div>
        <LineEditor items={isDirect ? items : items.filter(it => Number(it.warehouse_stock) > 0)} lines={lines} setLines={setLines} withCost={isDirect} createItem={isDirect ? createItem : null} showToast={showToast} />
        {(() => {
          const to = locations.find(l => l.id === dest);
          return to ? (
            <div style={{ fontSize: 12, color: ASHEN, marginBottom: 12 }}>
              Stock lands at <b>{to.name}</b>; billed to <b>{to.entity_name}</b>. Studios in the
              same country bill to the same entity, so the intercompany invoice is unaffected.
            </div>
          ) : null;
        })()}
        {!isDirect && <div style={{ fontSize: 12, color: ASHEN, marginBottom: 12 }}>Transfers use each item's current warehouse average cost — no cost entry needed. Only items with warehouse stock are shown.</div>}
        <button style={BTN} disabled={saving}>{saving ? 'Saving…' : (isDirect ? 'Record direct shipment' : 'Record transfer')}</button>
      </form>
    </div>
  );
}

// ── Invoices ───────────────────────────────────────────────────────────────────
function Invoices({ showToast }) {
  const [dest, setDest] = useState('HK');
  const [markup, setMarkup] = useState('0.05');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [detail, setDetail] = useState(null);

  const loadMarkup = useCallback(async () => {
    try { const r = await api.get('/ops/settings/markup'); setMarkup(r.data.intercompany_markup_pct); } catch (e) { /* default */ }
  }, []);
  const loadHistory = useCallback(async () => {
    try { const r = await api.get('/ops/invoices'); setHistory(r.data.invoices || []); } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { loadMarkup(); loadHistory(); }, [loadMarkup, loadHistory]);

  const runPreview = async () => {
    setLoading(true); setPreview(null);
    try {
      const r = await api.get(`/ops/invoice-preview?destination=${dest}&markup_pct=${markup}`);
      setPreview(r.data);
    } catch (err) { showToast(err.response?.data?.error || 'Preview failed', true); }
    finally { setLoading(false); }
  };

  const generate = async () => {
    if (!window.confirm(`Generate and finalise the ${dest} invoice? This resets the tally for the new month.`)) return;
    try {
      const r = await api.post('/ops/invoices/generate', { destination_entity: dest, markup_pct: markup });
      showToast(`Invoice ${r.data.invoice_number} generated — ${money(r.data.summary.grand_total_hkd)}`);
      setPreview(null); loadHistory();
    } catch (err) { showToast(err.response?.data?.error || 'Generation failed', true); }
  };

  const viewInvoice = async (id) => {
    try { const r = await api.get(`/ops/invoices/${id}`); setDetail(r.data); } catch (e) { console.error(e); }
  };

  const pctLabel = `${(Number(markup) * 100).toFixed(2)}%`;

  return (
    <>
      <div style={CARD}>
        <div style={{ ...LABEL, marginBottom: 16 }}>Month-end intercompany invoice</div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
          <div style={{ minWidth: 160 }}><label style={LABEL}>Subsidiary</label>
            <select style={INPUT} value={dest} onChange={e => { setDest(e.target.value); setPreview(null); }}>
              <option value="HK">Labret Limited (HK)</option><option value="SG">Labret Singapore PTE LTD</option>
            </select>
          </div>
          <div style={{ minWidth: 140 }}><label style={LABEL}>Cost-plus markup</label>
            <input style={INPUT} type="number" step="0.01" value={markup} onChange={e => setMarkup(e.target.value)} />
          </div>
          <button onClick={runPreview} style={BTN} disabled={loading}>{loading ? 'Loading…' : 'Preview'}</button>
        </div>
        <div style={{ fontSize: 12, color: ASHEN }}>Goods billed at cost +{pctLabel}. Shipping fronted by Group is billed at cost; shipping the store paid itself is shown but not billed.</div>
      </div>

      {preview && (
        <div style={CARD}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ ...LABEL, marginBottom: 0 }}>Preview — {dest}</div>
            {preview.summary.has_open_activity
              ? <button onClick={generate} style={{ ...BTN, background: GREEN }}>Generate & finalise</button>
              : <span style={{ color: ASHEN, fontSize: 13 }}>Nothing open to invoice.</span>}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead><tr><th style={TH}>Item</th><th style={TH}>Qty</th><th style={TH}>Unit cost</th><th style={TH}>Unit +{pctLabel}</th><th style={TH}>Line total</th></tr></thead>
              <tbody>
                {preview.goods.map((g, i) => (
                  <tr key={i}><td style={TD}>{g.item_name}</td><td style={TD}>{num(g.quantity)}</td><td style={TD}>{money(g.unit_cost_hkd)}</td><td style={TD}>{money(g.unit_price_hkd)}</td><td style={TD}>{money(g.line_total_hkd)}</td></tr>
                ))}
                {preview.shipping.map((s, i) => (
                  <tr key={'s' + i}><td style={{ ...TD, color: ASHEN }} colSpan={4}>Shipping (paid by {s.payer}){s.billed ? '' : ' — already settled'}</td><td style={TD}>{money(s.billed ? s.amount_hkd : 0)}</td></tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td style={{ ...TD, fontWeight: 700 }} colSpan={4}>Goods (cost {money(preview.summary.goods_cost_hkd)} +{pctLabel})</td><td style={{ ...TD, fontWeight: 700 }}>{money(preview.summary.goods_total_hkd)}</td></tr>
                <tr><td style={{ ...TD, fontWeight: 700 }} colSpan={4}>Shipping billed</td><td style={{ ...TD, fontWeight: 700 }}>{money(preview.summary.shipping_billed_hkd)}</td></tr>
                <tr><td style={{ ...TD, fontWeight: 800, fontSize: 15, color: GARNET }} colSpan={4}>Grand total owed to Labret Group</td><td style={{ ...TD, fontWeight: 800, fontSize: 15, color: GARNET }}>{money(preview.summary.grand_total_hkd)}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', ...LABEL, marginBottom: 0 }}>Past invoices</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr><th style={TH}>Invoice</th><th style={TH}>To</th><th style={TH}>Period</th><th style={TH}>Total</th><th style={TH}></th></tr></thead>
            <tbody>
              {history.length === 0 ? <tr><td style={{ ...TD, color: ASHEN }} colSpan={5}>No invoices yet.</td></tr>
                : history.map(inv => (
                  <tr key={inv.id}>
                    <td style={TD}>{inv.invoice_number}</td><td style={TD}>{inv.destination_entity}</td>
                    <td style={TD}>{inv.period_start || '?'} → {inv.period_end || '?'}</td>
                    <td style={TD}>{money(inv.grand_total_hkd)}</td>
                    <td style={TD}><button onClick={() => viewInvoice(inv.id)} style={BTN_GHOST}>View</button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 620, width: '100%', marginTop: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <h3 style={{ margin: 0, color: GARNET }}>{detail.invoice.invoice_number}</h3>
              <button onClick={() => setDetail(null)} style={BTN_GHOST}>Close</button>
            </div>
            <div style={{ fontSize: 13, color: ASHEN, marginBottom: 16 }}>
              To {detail.invoice.destination_entity} · {detail.invoice.period_start} → {detail.invoice.period_end} · markup {(Number(detail.invoice.markup_pct) * 100).toFixed(2)}%
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={TH}>Item</th><th style={TH}>Qty</th><th style={TH}>Unit price</th><th style={TH}>Total</th></tr></thead>
              <tbody>
                {detail.lines.map(l => (
                  <tr key={l.id}><td style={TD}>{l.item_name}</td><td style={TD}>{l.line_type === 'goods' ? num(l.quantity) : ''}</td><td style={TD}>{l.line_type === 'goods' ? money(l.unit_price_hkd) : ''}</td><td style={TD}>{money(l.line_total_hkd)}</td></tr>
                ))}
              </tbody>
              <tfoot><tr><td style={{ ...TD, fontWeight: 800, color: GARNET }} colSpan={3}>Grand total</td><td style={{ ...TD, fontWeight: 800, color: GARNET }}>{money(detail.invoice.grand_total_hkd)}</td></tr></tfoot>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ── Supplies: par levels, low-stock, weekly count ──────────────────────────────
function Supplies({ items, locations, showToast }) {
  const [studioId, setStudioId] = useState('');
  const [stock, setStock] = useState([]);
  const [low, setLow] = useState([]);
  const supplyItems = items.filter(i => i.category === 'supplies');
  const studio = locations.find(l => l.id === studioId);

  useEffect(() => { if (!studioId && locations.length) setStudioId(locations[0].id); }, [studioId, locations]);

  const load = useCallback(async () => {
    if (!studioId) return;
    try {
      const [s, l] = await Promise.all([
        api.get(`/ops/store-stock?studio_id=${studioId}&category=supplies`),
        api.get(`/ops/low-stock?studio_id=${studioId}`),
      ]);
      setStock(s.data.store_stock || []);
      setLow(l.data.low_stock || []);
    } catch (e) { console.error(e); }
  }, [studioId]);
  useEffect(() => { load(); }, [load]);

  const setPar = async (item_id, par_level) => {
    try { await api.put('/ops/store-stock/par', { item_id, studio_id: studioId, par_level }); showToast('Par level saved'); load(); }
    catch (err) { showToast(err.response?.data?.error || 'Failed', true); }
  };
  const submitCount = async (item_id, counted_qty) => {
    try { await api.post('/ops/stock-count', { item_id, studio_id: studioId, counted_qty }); showToast('Count submitted'); load(); }
    catch (err) { showToast(err.response?.data?.error || 'Failed', true); }
  };

  const stockByItem = Object.fromEntries(stock.map(s => [s.item_id, s]));
  const label = studio ? studio.name : '—';

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {locations.map(l => (
          <button key={l.id} onClick={() => setStudioId(l.id)} style={{ ...BTN_GHOST, ...(studioId === l.id ? { background: GARNET, color: '#fff', borderColor: GARNET } : {}) }}>{l.name}</button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: ASHEN, marginBottom: 16 }}>
        Par levels and counts are per studio — a newly opened studio starts with none
        rather than inheriting its country's.
      </div>

      {low.length > 0 && (
        <div style={{ ...CARD, borderLeft: `4px solid ${AMBER}` }}>
          <div style={{ ...LABEL, color: AMBER, marginBottom: 12 }}>⚠ Low stock — {label}</div>
          {low.map(l => (
            <div key={l.item_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5', flexWrap: 'wrap', gap: 6 }}>
              <span style={{ fontSize: 14, color: COAL }}>{l.name}</span>
              <span style={{ fontSize: 13, color: ASHEN }}>
                {num(l.quantity)} / par {num(l.par_level)} · <b style={{ color: GARNET }}>transfer {num(l.suggested_transfer)} {l.unit_of_measure}</b> (warehouse: {num(l.warehouse_stock)})
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', ...LABEL, marginBottom: 0 }}>Supplies — {label} (set par, submit weekly count)</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
            <thead><tr><th style={TH}>Item</th><th style={TH}>On hand</th><th style={TH}>Par level</th><th style={TH}>Weekly count</th></tr></thead>
            <tbody>
              {supplyItems.length === 0 ? <tr><td style={{ ...TD, color: ASHEN }} colSpan={4}>No supply items — add some in the Warehouse tab.</td></tr>
                : supplyItems.map(it => {
                  const s = stockByItem[it.id] || {};
                  return <SupplyRow key={it.id} item={it} stock={s} onPar={setPar} onCount={submitCount} />;
                })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function SupplyRow({ item, stock, onPar, onCount }) {
  const [par, setParVal] = useState(stock.par_level ?? '');
  const [count, setCount] = useState('');
  useEffect(() => { setParVal(stock.par_level ?? ''); }, [stock.par_level]);
  return (
    <tr>
      <td style={TD}>{item.name}</td>
      <td style={TD}>{num(stock.quantity || 0)} {item.unit_of_measure}</td>
      <td style={TD}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input style={{ ...INPUT, width: 80 }} type="number" step="any" value={par} onChange={e => setParVal(e.target.value)} />
          <button style={BTN_GHOST} onClick={() => onPar(item.id, par)}>Save</button>
        </div>
      </td>
      <td style={TD}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input style={{ ...INPUT, width: 90 }} type="number" step="any" placeholder="count" value={count} onChange={e => setCount(e.target.value)} />
          <button style={BTN_GHOST} onClick={() => { if (count !== '') { onCount(item.id, count); setCount(''); } }}>Submit</button>
        </div>
      </td>
    </tr>
  );
}
